import React, { useRef, useState } from 'react';
import { useLocale } from '@platform/core';

interface MagicGridToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  addable?: boolean;
  onAdd: () => void;
  importable?: boolean;
  onImport: (rows: Record<string, any>[]) => void;
  exportable?: boolean;
  onExport: () => void;
  settingsMode: boolean;
  onToggleSettings: () => void;
}

export default function MagicGridToolbar({
  search,
  onSearchChange,
  addable,
  onAdd,
  importable,
  onImport,
  exportable,
  onExport,
  settingsMode,
  onToggleSettings,
}: MagicGridToolbarProps) {
  const { t } = useLocale();
  const fileRef = useRef<HTMLInputElement>(null);
  const [importPreview, setImportPreview] = useState<Record<string, any>[] | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const parsed = parseCSV(text);
      setImportPreview(parsed);
    };
    reader.readAsText(file);
  };

  const commitImport = () => {
    if (importPreview) {
      onImport(importPreview);
      setImportPreview(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const cancelImport = () => {
    setImportPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <>
      <div className="mg-toolbar">
        {addable && (
          <button className="mg-btn mg-btn-primary" onClick={onAdd}>
            ➕ {t('common.add') || 'Add'}
          </button>
        )}
        <div className="mg-search-wrap">
          <span className="mg-search-icon">🔍</span>
          <input
            className="mg-search"
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('common.search') || 'Search...'}
          />
        </div>
        <div className="mg-toolbar-right">
          {importable && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              <button
                className="mg-btn mg-btn-outline"
                onClick={() => fileRef.current?.click()}
              >
                📥 {t('common.import') || 'Import'}
              </button>
            </>
          )}
          {exportable && (
            <button className="mg-btn mg-btn-outline" onClick={onExport}>
              📤 CSV
            </button>
          )}
          <button
            className={`mg-btn ${settingsMode ? 'mg-btn-primary' : 'mg-btn-outline'}`}
            onClick={onToggleSettings}
          >
            ⚙️
          </button>
        </div>
      </div>

      {/* Import Preview Modal */}
      {importPreview && (
        <div className="mg-modal-overlay">
          <div className="mg-modal">
            <h3>{t('common.importPreview') || 'Import Preview'}</h3>
            <p>
              {t('common.importWarning') ||
                'Warning: Import will add rows to the existing data.'}
            </p>
            <div className="mg-import-preview">
              <table className="mg-table">
                <thead>
                  <tr>
                    {importPreview.length > 0 &&
                      Object.keys(importPreview[0]).map((col) => (
                        <th key={col}>{col}</th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {importPreview.slice(0, 10).map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => (
                        <td key={j}>{String(val)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {importPreview.length > 10 && (
                <p className="mg-preview-note">
                  Showing first 10 of {importPreview.length} rows
                </p>
              )}
            </div>
            <div className="mg-modal-actions">
              <button className="mg-btn mg-btn-primary" onClick={commitImport}>
                {t('common.import') || 'Import'} ({importPreview.length} rows)
              </button>
              <button className="mg-btn mg-btn-outline" onClick={cancelImport}>
                {t('common.cancel') || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function parseCSV(text: string): Record<string, any>[] {
  const lines = text.split('\n').filter((l) => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map((line) => {
    const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
    const row: Record<string, any> = {};
    headers.forEach((h, i) => {
      const val = values[i] || '';
      // Try to parse numbers and booleans
      if (val === 'true') row[h] = true;
      else if (val === 'false') row[h] = false;
      else if (!isNaN(Number(val)) && val !== '') row[h] = Number(val);
      else row[h] = val;
    });
    return row;
  });
}
